import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { contentAPI } from '../services/api';
import { useToast } from './Toast';
import '../styles/WorkspaceChrome.css';
import './PublishPreflight.css';

const EMPTY_PACK = {
  generated_at: null,
  updated_at: null,
  seo: {
    meta_title: '',
    meta_description: '',
    url_slug: '',
    excerpt: '',
    focus_keywords: [],
  },
  social: {
    linkedin: '',
    x: '',
    bluesky: '',
    facebook: '',
  },
  citations: [],
  wordpress: {
    categories: [],
    tags: [],
  },
  media: [],
};

function normalizePack(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_PACK, seo: { ...EMPTY_PACK.seo }, social: { ...EMPTY_PACK.social }, wordpress: { ...EMPTY_PACK.wordpress } };
  }
  return {
    generated_at: raw.generated_at || null,
    updated_at: raw.updated_at || null,
    seo: {
      meta_title: raw.seo?.meta_title || '',
      meta_description: raw.seo?.meta_description || '',
      url_slug: raw.seo?.url_slug || '',
      excerpt: raw.seo?.excerpt || '',
      focus_keywords: Array.isArray(raw.seo?.focus_keywords)
        ? [...raw.seo.focus_keywords]
        : [],
    },
    social: {
      linkedin: raw.social?.linkedin || '',
      x: raw.social?.x || '',
      bluesky: raw.social?.bluesky || '',
      facebook: raw.social?.facebook || '',
    },
    citations: Array.isArray(raw.citations)
      ? raw.citations.map((c) => ({
          title: c.title || '',
          url: c.url || '',
          publisher_domain: c.publisher_domain || '',
          source_id: c.source_id || null,
          include: c.include !== false,
        }))
      : [],
    wordpress: {
      categories: Array.isArray(raw.wordpress?.categories)
        ? [...raw.wordpress.categories]
        : [],
      tags: Array.isArray(raw.wordpress?.tags) ? [...raw.wordpress.tags] : [],
    },
    media: Array.isArray(raw.media)
      ? raw.media.map((m) => ({
          kind: m.kind || 'stock_photo',
          suggestion: m.suggestion || '',
          search_query: m.search_query || '',
        }))
      : [],
  };
}

async function copyText(text, showToast, label = 'Copied') {
  const value = (text || '').toString();
  if (!value.trim()) {
    showToast('Nothing to copy.', 'warning');
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    showToast(label, 'success', 2000);
  } catch (err) {
    showToast('Copy failed — select and copy manually.', 'error');
  }
}

function FieldCopyRow({ label, hint, children, copyValue, showToast }) {
  return (
    <div className="pp-field">
      <div className="pp-field-head">
        <label className="pp-field-label">{label}</label>
        <button
          type="button"
          className="pp-copy-btn"
          onClick={() => void copyText(copyValue, showToast)}
        >
          Copy
        </button>
      </div>
      {hint && <p className="pp-field-hint">{hint}</p>}
      {children}
    </div>
  );
}

function PublishPreflight() {
  const { draftId } = useParams();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [pack, setPack] = useState(() => normalizePack(null));
  const [dirty, setDirty] = useState(false);
  const [keywordsText, setKeywordsText] = useState('');
  const [categoriesText, setCategoriesText] = useState('');
  const [tagsText, setTagsText] = useState('');

  const applyPack = useCallback((raw) => {
    const next = normalizePack(raw);
    setPack(next);
    setKeywordsText((next.seo.focus_keywords || []).join(', '));
    setCategoriesText((next.wordpress.categories || []).join(', '));
    setTagsText((next.wordpress.tags || []).join(', '));
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await contentAPI.getPublishPreflight(draftId);
      setTitle(res.data.title || '');
      setStatus(res.data.status || '');
      applyPack(res.data.publish_preflight);
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Failed to load publish checklist',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [draftId, applyPack, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateSeo = (key, value) => {
    setPack((prev) => ({
      ...prev,
      seo: { ...prev.seo, [key]: value },
    }));
    setDirty(true);
  };

  const updateSocial = (key, value) => {
    setPack((prev) => ({
      ...prev,
      social: { ...prev.social, [key]: value },
    }));
    setDirty(true);
  };

  const updateCitation = (idx, patch) => {
    setPack((prev) => {
      const citations = prev.citations.slice();
      citations[idx] = { ...citations[idx], ...patch };
      return { ...prev, citations };
    });
    setDirty(true);
  };

  const updateMedia = (idx, patch) => {
    setPack((prev) => {
      const media = prev.media.slice();
      media[idx] = { ...media[idx], ...patch };
      return { ...prev, media };
    });
    setDirty(true);
  };

  const buildPayload = () => {
    const keywords = keywordsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const categories = categoriesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const tags = tagsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      ...pack,
      seo: { ...pack.seo, focus_keywords: keywords },
      wordpress: { categories, tags },
    };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = buildPayload();
      const res = await contentAPI.updatePublishPreflight(draftId, payload);
      applyPack(res.data.publish_preflight);
      showToast('Publish checklist saved.', 'success');
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Save failed',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (
      !window.confirm(
        'Regenerate the full publish checklist from the current article and research? Your edits will be replaced.'
      )
    ) {
      return;
    }
    try {
      setRegenerating(true);
      const res = await contentAPI.regeneratePublishPreflight(draftId);
      applyPack(res.data.publish_preflight);
      showToast('Publish checklist regenerated.', 'success');
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Regenerate failed',
        'error'
      );
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="pp-page">
        <div className="pf-loading">Loading publish checklist…</div>
      </div>
    );
  }

  const hasPack =
    pack.seo.meta_title ||
    pack.seo.meta_description ||
    pack.citations.length > 0 ||
    pack.media.length > 0 ||
    pack.social.linkedin ||
    pack.wordpress.categories.length > 0;

  return (
    <div className="pp-page">
      <header className="pp-header">
        <div className="pp-header-main">
          <p className="pp-eyebrow">Publish checklist</p>
          <h1 className="pp-title">{title || 'Untitled draft'}</h1>
          <p className="pp-sub">
            Copy fields into your CMS while this page stays open beside the editor. Edits save to
            Racontera; nothing is pushed to WordPress from here.
          </p>
          <div className="pp-meta-row">
            {status && (
              <span className={`pp-badge status-${status}`}>
                {status === 'published' ? 'Published' : status === 'approved' ? 'In queue' : status}
              </span>
            )}
            {pack.generated_at && (
              <span className="pp-meta-muted">
                Generated {new Date(pack.generated_at).toLocaleString()}
              </span>
            )}
            {dirty && <span className="pp-badge pp-badge-dirty">Unsaved edits</span>}
          </div>
        </div>
        <div className="pp-header-actions">
          <Link to={`/research-brief/${draftId}`} className="pf-btn pf-btn-secondary">
            Workspace
          </Link>
          <Link to="/publishing-queue" className="pf-btn pf-btn-secondary">
            Publishing queue
          </Link>
          <button
            type="button"
            className="pf-btn pf-btn-secondary"
            onClick={() => void handleRegenerate()}
            disabled={regenerating || saving}
          >
            {regenerating ? 'Regenerating…' : hasPack ? 'Regenerate' : 'Generate'}
          </button>
          <button
            type="button"
            className="pf-btn pf-btn-primary"
            onClick={() => void handleSave()}
            disabled={saving || regenerating || !dirty}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {!hasPack && (
        <div className="pp-empty-banner" role="status">
          <p>
            No checklist yet. Generate one from the article and research sources, then copy into your
            CMS.
          </p>
          <button
            type="button"
            className="pf-btn pf-btn-primary"
            onClick={() => void handleRegenerate()}
            disabled={regenerating}
          >
            {regenerating ? 'Generating…' : 'Generate checklist'}
          </button>
        </div>
      )}

      <div className="pp-sections">
        <section className="pp-card">
          <h2>SEO</h2>
          <p className="pp-section-intro">
            Meta title, description, slug, excerpt, and focus keywords for the CMS / SEO plugin.
          </p>
          <FieldCopyRow
            label="Meta title"
            hint="Aim for ~50–60 characters."
            copyValue={pack.seo.meta_title}
            showToast={showToast}
          >
            <input
              className="pp-input"
              value={pack.seo.meta_title}
              onChange={(e) => updateSeo('meta_title', e.target.value)}
            />
            <span className="pp-charcount">{pack.seo.meta_title.length}</span>
          </FieldCopyRow>
          <FieldCopyRow
            label="Meta description"
            hint="Aim for ~150–160 characters."
            copyValue={pack.seo.meta_description}
            showToast={showToast}
          >
            <textarea
              className="pp-textarea"
              rows={3}
              value={pack.seo.meta_description}
              onChange={(e) => updateSeo('meta_description', e.target.value)}
            />
            <span className="pp-charcount">{pack.seo.meta_description.length}</span>
          </FieldCopyRow>
          <FieldCopyRow
            label="URL slug"
            copyValue={pack.seo.url_slug}
            showToast={showToast}
          >
            <input
              className="pp-input mono"
              value={pack.seo.url_slug}
              onChange={(e) => updateSeo('url_slug', e.target.value)}
              spellCheck={false}
            />
          </FieldCopyRow>
          <FieldCopyRow
            label="Excerpt"
            hint="Marketing excerpt for the CMS — not the executive summary."
            copyValue={pack.seo.excerpt}
            showToast={showToast}
          >
            <textarea
              className="pp-textarea"
              rows={3}
              value={pack.seo.excerpt}
              onChange={(e) => updateSeo('excerpt', e.target.value)}
            />
          </FieldCopyRow>
          <FieldCopyRow
            label="Focus keywords"
            hint="Comma-separated."
            copyValue={keywordsText}
            showToast={showToast}
          >
            <input
              className="pp-input"
              value={keywordsText}
              onChange={(e) => {
                setKeywordsText(e.target.value);
                setDirty(true);
              }}
            />
          </FieldCopyRow>
        </section>

        <section className="pp-card">
          <h2>Social teasers</h2>
          <p className="pp-section-intro">
            Platform-ready copy to paste when sharing. Adjust tone before posting.
          </p>
          {[
            { key: 'linkedin', label: 'LinkedIn', rows: 4 },
            { key: 'x', label: 'X (Twitter)', rows: 2, max: 280 },
            { key: 'bluesky', label: 'Bluesky', rows: 3 },
            { key: 'facebook', label: 'Facebook', rows: 4 },
          ].map(({ key, label, rows, max }) => (
            <FieldCopyRow
              key={key}
              label={label}
              hint={max ? `Keep under ${max} characters.` : null}
              copyValue={pack.social[key]}
              showToast={showToast}
            >
              <textarea
                className="pp-textarea"
                rows={rows}
                value={pack.social[key]}
                onChange={(e) => updateSocial(key, e.target.value)}
              />
              {max != null && (
                <span
                  className={`pp-charcount${pack.social[key].length > max ? ' over' : ''}`}
                >
                  {pack.social[key].length}/{max}
                </span>
              )}
            </FieldCopyRow>
          ))}
        </section>

        <section className="pp-card">
          <h2>Citation links</h2>
          <p className="pp-section-intro">
            External sources to add as links or a resources section on the WordPress side. Uncheck
            items you will not use.
          </p>
          {pack.citations.length === 0 ? (
            <p className="pp-muted">No citation sources in this pack.</p>
          ) : (
            <ul className="pp-cite-list">
              {pack.citations.map((c, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${c.url}-${idx}`} className={!c.include ? 'pp-cite-off' : ''}>
                  <label className="pp-cite-include">
                    <input
                      type="checkbox"
                      checked={c.include}
                      onChange={(e) => updateCitation(idx, { include: e.target.checked })}
                    />
                    <span className="visually-hidden">Include</span>
                  </label>
                  <div className="pp-cite-body">
                    <input
                      className="pp-input"
                      value={c.title}
                      onChange={(e) => updateCitation(idx, { title: e.target.value })}
                      placeholder="Title"
                    />
                    <input
                      className="pp-input mono"
                      value={c.url}
                      onChange={(e) => updateCitation(idx, { url: e.target.value })}
                      spellCheck={false}
                    />
                    {c.publisher_domain && (
                      <span className="pp-cite-domain">{c.publisher_domain}</span>
                    )}
                  </div>
                  <div className="pp-cite-actions">
                    <button
                      type="button"
                      className="pp-copy-btn"
                      onClick={() => void copyText(c.url, showToast, 'URL copied')}
                    >
                      Copy URL
                    </button>
                    <button
                      type="button"
                      className="pp-copy-btn"
                      onClick={() =>
                        void copyText(
                          `[${c.title || c.url}](${c.url})`,
                          showToast,
                          'Markdown link copied'
                        )
                      }
                    >
                      MD
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {pack.citations.some((c) => c.include) && (
            <button
              type="button"
              className="pf-btn pf-btn-secondary pp-copy-all"
              onClick={() =>
                void copyText(
                  pack.citations
                    .filter((c) => c.include)
                    .map((c) => `${c.title || c.url}\n${c.url}`)
                    .join('\n\n'),
                  showToast,
                  'All included citations copied'
                )
              }
            >
              Copy all included
            </button>
          )}
        </section>

        <section className="pp-card">
          <h2>WordPress taxonomy</h2>
          <p className="pp-section-intro">
            Suggested category and tag <em>names</em> — match them to your site&apos;s taxonomy
            manually.
          </p>
          <FieldCopyRow
            label="Categories"
            hint="Comma-separated suggestions (1–3 typical)."
            copyValue={categoriesText}
            showToast={showToast}
          >
            <input
              className="pp-input"
              value={categoriesText}
              onChange={(e) => {
                setCategoriesText(e.target.value);
                setDirty(true);
              }}
            />
          </FieldCopyRow>
          <FieldCopyRow
            label="Tags"
            hint="Comma-separated suggestions."
            copyValue={tagsText}
            showToast={showToast}
          >
            <input
              className="pp-input"
              value={tagsText}
              onChange={(e) => {
                setTagsText(e.target.value);
                setDirty(true);
              }}
            />
          </FieldCopyRow>
        </section>

        <section className="pp-card">
          <h2>Media recommendations</h2>
          <p className="pp-section-intro">
            Ideas for featured images, stock search, diagrams, or screenshots — not assets.
          </p>
          {pack.media.length === 0 ? (
            <p className="pp-muted">No media suggestions yet.</p>
          ) : (
            <ul className="pp-media-list">
              {pack.media.map((m, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={idx} className="pp-media-card">
                  <div className="pp-media-kind-row">
                    <select
                      className="pp-select"
                      value={m.kind}
                      onChange={(e) => updateMedia(idx, { kind: e.target.value })}
                    >
                      <option value="featured">Featured</option>
                      <option value="stock_photo">Stock photo</option>
                      <option value="diagram">Diagram</option>
                      <option value="screenshot">Screenshot</option>
                    </select>
                    {m.search_query && (
                      <button
                        type="button"
                        className="pp-copy-btn"
                        onClick={() =>
                          void copyText(m.search_query, showToast, 'Search query copied')
                        }
                      >
                        Copy search
                      </button>
                    )}
                  </div>
                  <textarea
                    className="pp-textarea"
                    rows={2}
                    value={m.suggestion}
                    onChange={(e) => updateMedia(idx, { suggestion: e.target.value })}
                  />
                  <input
                    className="pp-input"
                    placeholder="Stock search query (optional)"
                    value={m.search_query}
                    onChange={(e) => updateMedia(idx, { search_query: e.target.value })}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

export default PublishPreflight;
